import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Плашка «не удалось загрузить» + «Повторить» — один компонент на все точки
 * дашборда (AK-790 re-land #670, вердикт head-web: сырой `<button>` с ручными
 * классами в каждом catch'е — не DS). Текст берётся из `classifyApiError`
 * (`@/lib/apiError`): исходный `err` сюда не попадает никогда.
 *
 * variant:
 *   · "block"  — центрированная плашка ВМЕСТО контента (данных ещё нет);
 *   · "inline" — компактная строка ПОВЕРХ таблицы/графика: прошлые данные
 *     ЕСТЬ, прятать их нельзя, но и выдавать за свежие — тоже (refetch при
 *     смене хозяйства упал → цифры прежнего хозяйства помечены как стухшие).
 */
export interface ErrorStateProps {
  message: string;
  onRetry?: () => void;
  variant?: 'block' | 'inline';
  className?: string;
}

export function ErrorState({
  message,
  onRetry,
  variant = 'block',
  className,
}: ErrorStateProps) {
  if (variant === 'inline') {
    return (
      <Alert variant="destructive" className={cn('py-2', className)}>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
          <span>{message}</span>
          {onRetry && (
            <Button
              variant="link"
              size="sm"
              onClick={onRetry}
              className="h-auto p-0 text-primary"
            >
              Повторить
            </Button>
          )}
        </AlertDescription>
      </Alert>
    );
  }
  return (
    <div role="alert" className={cn('py-12 text-center', className)}>
      <p className="text-sm text-destructive">{message}</p>
      {onRetry && (
        <Button
          variant="link"
          size="sm"
          onClick={onRetry}
          className="mt-3 text-primary"
        >
          Повторить
        </Button>
      )}
    </div>
  );
}

export default ErrorState;
